import { GraftCodePrimitiveType } from '@graftcode/protocol'

import type { ParameterShape } from '@/core/utils/mappers/formatTypeUsage'

import { getArrayBrackets, getNestedElementDisplay } from './arrayBrackets'
import { type CodeSign } from './codeSigns'

export function formatLiteralElement(
  raw: unknown,
  shape: ParameterShape,
  technology: string,
  signs: CodeSign,
  elementDisplay: string
): string {
  if (shape.kind === 'array') {
    if (!Array.isArray(raw)) return defaultLiteral(raw, technology, signs)
    return renderArrayLiteral(raw, shape, technology, signs, elementDisplay)
  }

  if (shape.kind === 'primitive') {
    return renderPrimitiveLiteral(raw, shape.primitiveType, technology, signs)
  }

  return defaultLiteral(raw, technology, signs)
}

export function renderPrimitiveLiteral(
  value: unknown,
  primitiveType: number | undefined,
  technology: string,
  signs: CodeSign
): string {
  if (value === null) return renderNullLiteral(technology)

  switch (primitiveType) {
    case GraftCodePrimitiveType.STRING:
    case GraftCodePrimitiveType.CHAR:
      return `${signs.stringSign}${String(value)}${signs.stringSign}`
    case GraftCodePrimitiveType.BOOLEAN:
      return renderBooleanLiteral(value, technology)
    case GraftCodePrimitiveType.INTEGER:
    case GraftCodePrimitiveType.LONG:
    case GraftCodePrimitiveType.BYTE:
    case GraftCodePrimitiveType.UNSIGNED_INTEGER:
    case GraftCodePrimitiveType.UNSIGNED_LONG_LONG:
    case GraftCodePrimitiveType.FLOAT:
    case GraftCodePrimitiveType.DOUBLE:
      return String(value)
    default:
      return defaultLiteral(value, technology, signs)
  }
}

function renderBooleanLiteral(value: unknown, technology: string): string {
  const truthy = value === true || value === 'true' || value === 1 || value === '1'
  if (technology === 'pypi') return truthy ? 'True' : 'False'
  return truthy ? 'true' : 'false'
}

function renderNullLiteral(technology: string): string {
  switch (technology) {
    case 'pypi':
      return 'None'
    case 'ruby':
      return 'nil'
    default:
      return 'null'
  }
}

function defaultLiteral(value: unknown, technology: string, signs: CodeSign): string {
  if (value === null) return renderNullLiteral(technology)
  if (typeof value === 'string') return `${signs.stringSign}${value}${signs.stringSign}`
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) {
    const brackets = getArrayBrackets(technology, '')
    return `${brackets.open}${value.map((v) => defaultLiteral(v, technology, signs)).join(', ')}${brackets.close}`
  }
  return String(value)
}

export function renderArrayLiteral(
  items: unknown[],
  shape: Extract<ParameterShape, { kind: 'array' }>,
  technology: string,
  signs: CodeSign,
  elementDisplay: string
): string {
  const brackets = getArrayBrackets(technology, elementDisplay)
  const inner = items
    .map((item) =>
      formatLiteralElement(item, shape.element, technology, signs, getNestedElementDisplay(shape, technology))
    )
    .join(brackets.separator)
  return `${brackets.open}${inner}${brackets.close}`
}
